import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { collection, limit, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import "./FleetScan.css";

const sortByDateDesc = (items) =>
  [...items].sort((a, b) => {
    const aDate = a.createdAt?.toDate?.() || new Date(a.dateTime || a.dateReceived || 0);
    const bDate = b.createdAt?.toDate?.() || new Date(b.dateTime || b.dateReceived || 0);
    return bDate - aDate;
  });

export default function FleetScan() {
  const { type, number } = useParams();
  const fleetType = (type || "").toLowerCase() === "tanker" ? "Tanker" : "Vehicle";
  const collectionName = fleetType === "Tanker" ? "tankers" : "trucks";
  const decodedNumber = useMemo(() => decodeURIComponent(number || ""), [number]);

  const [fleetItem, setFleetItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vehicleFills, setVehicleFills] = useState([]);
  const [pumpFills, setPumpFills] = useState([]);

  useEffect(() => {
    if (!decodedNumber) return undefined;

    const fleetQuery = query(
      collection(db, collectionName),
      where("truckNumber", "==", decodedNumber),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      fleetQuery,
      (snapshot) => {
        setFleetItem(snapshot.empty ? null : { id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
        setLoading(false);
      },
      () => setLoading(false)
    );

    return () => unsubscribe();
  }, [collectionName, decodedNumber]);

  useEffect(() => {
    if (!decodedNumber) return undefined;

    const fieldName = fleetType === "Tanker" ? "tankerId" : "truckId";
    const fillsQuery = query(
      collection(db, "tankerFillOperations"),
      where(fieldName, "==", decodedNumber)
    );

    const unsubscribe = onSnapshot(fillsQuery, (snapshot) => {
      const data = snapshot.docs.map((docu) => ({ id: docu.id, ...docu.data() }));
      setVehicleFills(sortByDateDesc(data));
    });

    return () => unsubscribe();
  }, [decodedNumber, fleetType]);

  useEffect(() => {
    if (!decodedNumber || fleetType !== "Tanker") {
      setPumpFills([]);
      return undefined;
    }

    const pumpQuery = query(
      collection(db, "tankerFillOperationsPump"),
      where("tankerId", "==", decodedNumber)
    );

    const unsubscribe = onSnapshot(pumpQuery, (snapshot) => {
      const data = snapshot.docs.map((docu) => ({ id: docu.id, ...docu.data() }));
      setPumpFills(sortByDateDesc(data));
    });

    return () => unsubscribe();
  }, [decodedNumber, fleetType]);

  const totalVehicleOil = vehicleFills.reduce((sum, record) => sum + Number(record.filledOil || 0), 0);
  const totalPumpOil = pumpFills.reduce((sum, record) => sum + Number(record.filledOil || 0), 0);

  return (
    <main className="scan-page">
      <section className="scan-header">
        <div>
          <p>{fleetType} QR Scan</p>
          <h1>{decodedNumber || "Fleet Details"}</h1>
        </div>
        <Link to="/fleet">Back to Fleet</Link>
      </section>

      {loading ? (
        <div className="scan-card">Loading live Firebase data...</div>
      ) : !fleetItem ? (
        <div className="scan-card">No {fleetType.toLowerCase()} found for this QR code.</div>
      ) : (
        <>
          <section className="scan-card scan-details">
            <div>
              <span>Model</span>
              <strong>{fleetItem.model || "N/A"}</strong>
            </div>
            <div>
              <span>Location</span>
              <strong>{fleetItem.location || "N/A"}</strong>
            </div>
            <div>
              <span>Driver</span>
              <strong>{fleetItem.driverName || "N/A"}</strong>
            </div>
            <div>
              <span>Status</span>
              <strong>{fleetItem.status || "N/A"}</strong>
            </div>
            {fleetType === "Tanker" ? (
              <>
                <div>
                  <span>Capacity</span>
                  <strong>{fleetItem.capacity || 0} L</strong>
                </div>
                <div>
                  <span>Remaining Oil</span>
                  <strong>{fleetItem.remainingOil ?? 0} L</strong>
                </div>
              </>
            ) : (
              <div>
                <span>Current Reading</span>
                <strong>{fleetItem.currentReading || 0} km</strong>
              </div>
            )}
          </section>

          <section className="scan-summary">
            <div>
              <span>{fleetType === "Tanker" ? "Oil Given To Vehicles" : "Oil Filled In Vehicle"}</span>
              <strong>{totalVehicleOil} L</strong>
            </div>
            {fleetType === "Tanker" && (
              <div>
                <span>Oil Filled From Pump</span>
                <strong>{totalPumpOil} L</strong>
              </div>
            )}
          </section>

          <section className="scan-card">
            <h2>{fleetType === "Tanker" ? "Tanker To Vehicle Records" : "Vehicle Oil Fill Records"}</h2>
            <div className="scan-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Tanker</th>
                    <th>Vehicle</th>
                    <th>Filled</th>
                    <th>Reading</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicleFills.length ? (
                    vehicleFills.map((record) => (
                      <tr key={record.id}>
                        <td>{record.dateTime || "-"}</td>
                        <td>{record.tankerId || "-"}</td>
                        <td>{record.truckId || "-"}</td>
                        <td>{record.filledOil || 0} L</td>
                        <td>{record.currentReading ?? "-"}</td>
                        <td>{record.source || "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">No oil records found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {fleetType === "Tanker" && (
            <section className="scan-card">
              <h2>Pump To Tanker Records</h2>
              <div className="scan-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Product</th>
                      <th>Old Oil</th>
                      <th>Filled</th>
                      <th>Final Oil</th>
                      <th>Pump</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pumpFills.length ? (
                      pumpFills.map((record) => (
                        <tr key={record.id}>
                          <td>{record.dateReceived || "-"}</td>
                          <td>{record.product || "-"}</td>
                          <td>{record.oldOil || 0} L</td>
                          <td>{record.filledOil || 0} L</td>
                          <td>{record.finalOil || 0} L</td>
                          <td>{record.pumpName || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6">No pump records found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </main>
  );
}
